/**
 * Velociraptor API service
 *
 * Provides functions for interacting with Velociraptor server for artefact listing,
 * collector building, and offline collection packages.
 */

import { apiClient } from './api'
import type {
  VelociraptorStatus,
  VqlArtefact,
  BuiltPackage,
  CollectorBuildResult,
  BuildCollectorInput,
  BuildFromTemplateInput,
  ListArtefactsParams,
  ArtefactsListResponse,
} from '../types/velociraptor'

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// HEALTH & STATUS API
// =============================================================================

/**
 * Check Velociraptor server health and connection status
 */
export async function getHealth(): Promise<VelociraptorStatus> {
  return apiClient.get<VelociraptorStatus>('/velociraptor/health')
}

// =============================================================================
// ARTEFACT API
// =============================================================================

/**
 * List available VQL artefacts
 */
export async function listArtefacts(params?: ListArtefactsParams): Promise<ArtefactsListResponse> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<ArtefactsListResponse>(`/velociraptor/artefacts${query}`)
}

/**
 * Get a single artefact by name
 */
export async function getArtefact(name: string): Promise<VqlArtefact> {
  return apiClient.get<VqlArtefact>(`/velociraptor/artefacts/${encodeURIComponent(name)}`)
}

// =============================================================================
// COLLECTOR BUILD API
// =============================================================================

/**
 * Build a custom collector package
 */
export async function buildCollector(input: BuildCollectorInput): Promise<CollectorBuildResult> {
  return apiClient.post<CollectorBuildResult>('/velociraptor/collectors/build', input)
}

/**
 * Build collector from a collection request template
 */
export async function buildFromTemplate(
  input: BuildFromTemplateInput
): Promise<CollectorBuildResult> {
  return apiClient.post<CollectorBuildResult>('/velociraptor/collectors/build-from-template', input)
}

/**
 * Get build status for a collector
 */
export async function getBuildStatus(buildId: string): Promise<BuiltPackage> {
  return apiClient.get<BuiltPackage>(`/velociraptor/collectors/${buildId}/status`)
}

/**
 * Poll build status until complete or failed
 */
export async function pollBuildStatus(
  buildId: string,
  options?: {
    intervalMs?: number
    maxAttempts?: number
    onProgress?: (status: BuiltPackage) => void
  }
): Promise<BuiltPackage> {
  const { intervalMs = 2000, maxAttempts = 60, onProgress } = options ?? {}

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const status = await getBuildStatus(buildId)

    if (onProgress) {
      onProgress(status)
    }

    if (status.status === 'completed' || status.status === 'failed') {
      return status
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }

  throw new Error('Build timed out')
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Group artefacts by category
 */
export function groupArtefactsByCategory(artefacts: VqlArtefact[]): Record<string, VqlArtefact[]> {
  const grouped: Record<string, VqlArtefact[]> = {}

  for (const artefact of artefacts) {
    const category = artefact.category ?? 'Uncategorised'
    grouped[category] ??= []
    grouped[category].push(artefact)
  }

  // Sort artefacts within each category
  for (const category of Object.keys(grouped)) {
    const categoryArtefacts = grouped[category]
    if (categoryArtefacts) {
      categoryArtefacts.sort((left, right) => left.name.localeCompare(right.name))
    }
  }

  return grouped
}

/**
 * Filter artefacts by search term
 */
export function filterArtefacts(artefacts: VqlArtefact[], search: string): VqlArtefact[] {
  const term = search.toLowerCase()
  return artefacts.filter(
    (art) =>
      art.name.toLowerCase().includes(term) ||
      (art.description?.toLowerCase().includes(term) ?? false) ||
      (art.category?.toLowerCase().includes(term) ?? false)
  )
}

/**
 * Get suggested artefacts for a device type
 */
export function getSuggestedArtefacts(artefacts: VqlArtefact[], deviceType: string): VqlArtefact[] {
  const type = deviceType.toLowerCase()

  if (type.includes('windows')) {
    return artefacts.filter(
      (art) => (art.category?.startsWith('Windows.') ?? false) || art.name.startsWith('Windows.')
    )
  }

  if (type.includes('linux') || type.includes('ubuntu') || type.includes('debian')) {
    return artefacts.filter(
      (art) => (art.category?.startsWith('Linux.') ?? false) || art.name.startsWith('Linux.')
    )
  }

  if (type.includes('mac') || type.includes('darwin') || type.includes('osx')) {
    return artefacts.filter(
      (art) => (art.category?.startsWith('MacOS.') ?? false) || art.name.startsWith('MacOS.')
    )
  }

  // Return generic forensics artefacts for unknown types
  return artefacts.filter(
    (art) =>
      (art.category?.includes('Generic') ?? false) || (art.category?.includes('Forensic') ?? false)
  )
}
